/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo } from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSessionStore } from '../session/store/useSessionStore';
import {
  CatalogScreen,
  CashManagementScreen,
  ExpensesScreen,
  PurchasesScreen,
  CustomersScreen,
  InventoryScreen,
  MySupportReportsScreen,
  AiChatScreen,
  OnlineOrdersScreen,
  OnlineComplaintsScreen,
  ConsultationsScreen,
  QuotesScreen,
  AppointmentsScreen,
  WorkOrdersScreen,
  MyCommissionsScreen,
  CommissionPlansScreen,
  SettlementDetailScreen,
  BoothRentalsScreen,
  PosScreen,
  ProfileScreen,
  PromotionsListScreen,
  PriceListsListScreen,
  LoyaltyProgramScreen,
  ReportsScreen,
  SalesListScreen,
} from './screen-registry';
import { DiningTablesScreen } from '../../hospitality/screens/DiningTablesScreen';
import { KitchenQueueScreen } from '../../hospitality/screens/KitchenQueueScreen';
import { MoreHubScreen } from './screens/MoreHubScreen';
import { HomeScreen } from './screens/HomeScreen';
import { OrdersInboxScreen } from './screens/OrdersInboxScreen';
import { CommissionsHubScreen } from './screens/CommissionsHubScreen';
import { PricingHubScreen } from './screens/PricingHubScreen';
import { CatalogHubScreen } from './screens/CatalogHubScreen';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { Ionicons } from '@expo/vector-icons';
import { useTabScreenRefreshContext } from './TabScreenRefreshContext';
import {
  LastMainTabProvider,
  useLastMainTabOptional,
} from './LastMainTabContext';
import { MoreTabBarButton } from './MoreTabBarButton';
import {
  getMobileTabManifestItems,
  MOBILE_MORE_TAB_ROUTE,
  NAVIGATION_MANIFEST,
  resolveInitialMainTabRoute,
  shouldRegisterHiddenTab,
  shouldRegisterOperationModeHiddenTab,
  shouldShowMobileMoreTab,
  WEB_SIDEBAR_BREAKPOINT,
  type MainTabsRouteName,
  type NavigationManifestItem,
} from './navigationManifest';
import { useNavVisibilityContext } from './useNavVisibilityContext';
import { useStorefrontPublishBootstrap } from '../../onboarding/application/useStorefrontPublishBootstrap';
import { useSupportUnreadCount } from '../providers/SupportUnreadProvider';
import { usePendingOrdersCount } from '../providers/PendingOrdersCountProvider';
import { useKitchenTicketCount } from '../../hospitality/providers/KitchenTicketCountProvider';
import { SupportUnreadIconAnchor } from '../support/components/SupportUnreadBadge';
import { AppTabHeader, getOperationalHeaderBarHeight } from './AppTabHeader';
import { withScreenPageTitle } from './withScreenPageTitle';
import { useTopChromeOffset } from './TopChromeOffsetContext';
import { useBusinessProfileStore } from '../session/store/useBusinessProfileStore';
import type { BusinessCapability } from '../session/domain/businessProfile';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import type { GlobalFeatureFlagsMap } from '../feature-flags/domain/globalFeatureFlags';

const Tab = createBottomTabNavigator();

const withTabPageTitle = <P extends object>(Screen: React.ComponentType<P>) =>
  withScreenPageTitle(Screen, { hideBodyTitle: true });

const TAB_COMPONENTS: Record<string, React.ComponentType<object>> = {
  Home: withTabPageTitle(HomeScreen),
  POS: withTabPageTitle(PosScreen),
  // El hub monta Catálogo + Opciones de menú + Combos (gated por módulo/capability).
  Catalog: withTabPageTitle(CatalogHubScreen),
  Inventory: withTabPageTitle(InventoryScreen),
  Customers: withTabPageTitle(CustomersScreen),
  Reports: withTabPageTitle(ReportsScreen),
  CashManagement: withTabPageTitle(CashManagementScreen),
  SalesList: withTabPageTitle(SalesListScreen),
  Expenses: withTabPageTitle(ExpensesScreen),
  Purchases: withTabPageTitle(PurchasesScreen),
  // El hub Precios monta Promociones/Listas/Fidelización — las rutas hijas
  // siguen registradas como tabs ocultos para deep-links.
  PricingHub: withTabPageTitle(PricingHubScreen),
  PromotionsList: withTabPageTitle(PromotionsListScreen),
  PriceListsList: withTabPageTitle(PriceListsListScreen),
  LoyaltyProgram: withTabPageTitle(LoyaltyProgramScreen),
  SupportReports: withTabPageTitle(MySupportReportsScreen),
  AiChat: withTabPageTitle(AiChatScreen),
  Profile: withTabPageTitle(ProfileScreen),
  OrdersInbox: withTabPageTitle(OrdersInboxScreen),
  OnlineOrders: withTabPageTitle(OnlineOrdersScreen),
  OnlineComplaints: withTabPageTitle(OnlineComplaintsScreen),
  Consultations: withTabPageTitle(ConsultationsScreen),
  Quotes: withTabPageTitle(QuotesScreen),
  Appointments: withTabPageTitle(AppointmentsScreen),
  WorkOrders: withTabPageTitle(WorkOrdersScreen),
  // El hub monta el dashboard + las pantallas absorbidas (Mis comisiones,
  // Planes, Alquileres) — las rutas hijas siguen registradas como tabs ocultos.
  CommissionsDashboard: withTabPageTitle(CommissionsHubScreen),
  MyCommissions: withTabPageTitle(MyCommissionsScreen),
  CommissionPlans: withTabPageTitle(CommissionPlansScreen),
  BoothRentals: withTabPageTitle(BoothRentalsScreen),
  SettlementDetail: SettlementDetailScreen,
  DiningTables: withTabPageTitle(DiningTablesScreen),
  KitchenQueue: withTabPageTitle(KitchenQueueScreen),
  [MOBILE_MORE_TAB_ROUTE]: withTabPageTitle(MoreHubScreen),
};

const HIDDEN_TAB_ROUTES = [
  'Reports',
  'CashManagement',
  'SalesList',
  'Expenses',
  'Purchases',
  'PricingHub',
  'PromotionsList',
  'PriceListsList',
  'LoyaltyProgram',
  'SupportReports',
  'AiChat',
  'Profile',
  'OrdersInbox',
  'OnlineOrders',
  'OnlineComplaints',
  'Consultations',
  'Quotes',
  'Appointments',
  'WorkOrders',
  'CommissionsDashboard',
  'MyCommissions',
  'CommissionPlans',
  'BoothRentals',
  'SettlementDetail',
  'Catalog',
  'Inventory',
  'Customers',
  'DiningTables',
  'KitchenQueue',
] as const;

export type MainTabsProps = {
  entitlements: PlanEntitlementsRow | null;
  globalFeatureFlags: GlobalFeatureFlagsMap | null;
};

const MainTabsInner = ({ entitlements, globalFeatureFlags }: MainTabsProps) => {
  const insets = useSafeAreaInsets();
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const currentBranch = branches.find((branch) => branch.id === currentBranchId);
  const { setActiveTabName } = useTabScreenRefreshContext();
  const tabBarBottomInset = Math.max(insets.bottom, Platform.OS === 'web' ? 16 : 0);
  const tabBarPaddingTop = 8;
  const tabBarInnerRowHeight = 56;
  const tabBarHeight = tabBarInnerRowHeight + tabBarPaddingTop + tabBarBottomInset;

  useStorefrontPublishBootstrap();
  const navCtx = useNavVisibilityContext(entitlements, globalFeatureFlags);
  const profileCapabilities = useBusinessProfileStore((s) => s.profile?.capabilities);
  const rubroCapabilities = useMemo(
    () => new Set<BusinessCapability>(profileCapabilities ?? []),
    [profileCapabilities],
  );

  const visibleTabItems = useMemo(() => getMobileTabManifestItems(navCtx), [navCtx]);
  const showMoreTab = shouldShowMobileMoreTab(navCtx);
  const initialMainTabRoute = useMemo(() => resolveInitialMainTabRoute(navCtx), [navCtx]);

  const tabScreens = useMemo(() => {
    const screens: Array<{
      item: NavigationManifestItem | { route: string; label: string; shortLabel?: string; icon: keyof typeof Ionicons.glyphMap };
      hidden?: boolean;
    }> = [];
    const seen = new Set<string>();
    for (const item of visibleTabItems) {
      screens.push({ item });
      seen.add(item.route);
    }
    if (showMoreTab) {
      screens.push({
        item: {
          route: MOBILE_MORE_TAB_ROUTE,
          label: 'Más',
          icon: 'apps',
        },
      });
      seen.add(MOBILE_MORE_TAB_ROUTE);
    }
    for (const route of HIDDEN_TAB_ROUTES) {
      if (seen.has(route)) continue;
      const asHiddenMore = shouldRegisterHiddenTab(navCtx, route);
      const asOperationModeHidden = shouldRegisterOperationModeHiddenTab(
        navCtx,
        route,
        rubroCapabilities,
      );
      const asOrdersFallback =
        route === 'OrdersInbox' &&
        navCtx.publicStoreFeaturesAllowed &&
        !visibleTabItems.some((i) => i.route === route);
      if (!asHiddenMore && !asOrdersFallback && !asOperationModeHidden) continue;
      const manifestItem = NAVIGATION_MANIFEST.find((m) => m.route === route);
      screens.push({
        item: {
          route,
          label: manifestItem?.label ?? route,
          icon: (manifestItem?.icon ?? 'apps') as keyof typeof Ionicons.glyphMap,
        },
        hidden: true,
      });
      seen.add(route);
    }
    if (!seen.has('Profile')) {
      const manifestItem = NAVIGATION_MANIFEST.find((m) => m.route === 'Profile');
      screens.push({
        item: {
          route: 'Profile',
          label: manifestItem?.label ?? 'Cuenta',
          icon: (manifestItem?.icon ?? 'person-circle-outline') as keyof typeof Ionicons.glyphMap,
        },
        hidden: true,
      });
      seen.add('Profile');
    }
    // Compras entra al sidebar cuando hidrata inventory+expenses; el Tab.Navigator
    // no toma pantallas nuevas después del mount. Registrarla siempre (oculta).
    if (!seen.has('Purchases')) {
      const manifestItem = NAVIGATION_MANIFEST.find((m) => m.route === 'Purchases');
      screens.push({
        item: {
          route: 'Purchases',
          label: manifestItem?.label ?? 'Compras',
          icon: (manifestItem?.icon ?? 'cart-outline') as keyof typeof Ionicons.glyphMap,
        },
        hidden: true,
      });
      seen.add('Purchases');
    }
    return screens;
  }, [visibleTabItems, showMoreTab, navCtx, rubroCapabilities]);

  const { width: windowWidth } = useWindowDimensions();
  const hideTabBarForSidebar = Platform.OS === 'web' && windowWidth >= WEB_SIDEBAR_BREAKPOINT;

  return (
    <LastMainTabProvider>
      <MainTabsTabNavigator
        tabScreens={tabScreens}
        initialMainTabRoute={initialMainTabRoute}
        hideTabBarForSidebar={hideTabBarForSidebar}
        tabBarHeight={tabBarHeight}
        tabBarPaddingTop={tabBarPaddingTop}
        tabBarBottomInset={tabBarBottomInset}
        currentBranchName={currentBranch?.name}
        setActiveTabName={setActiveTabName}
      />
    </LastMainTabProvider>
  );
};

type TabNavigatorContentProps = {
  tabScreens: Array<{
    item: NavigationManifestItem | { route: string; label: string; shortLabel?: string; icon: keyof typeof Ionicons.glyphMap };
    hidden?: boolean;
  }>;
  initialMainTabRoute: MainTabsRouteName;
  hideTabBarForSidebar: boolean;
  tabBarHeight: number;
  tabBarPaddingTop: number;
  tabBarBottomInset: number;
  currentBranchName?: string;
  setActiveTabName: (name: string) => void;
};

function MainTabsTabNavigator({
  tabScreens,
  initialMainTabRoute,
  hideTabBarForSidebar,
  tabBarHeight,
  tabBarPaddingTop,
  tabBarBottomInset,
  currentBranchName,
  setActiveTabName,
}: TabNavigatorContentProps) {
  const insets = useSafeAreaInsets();
  const { globalBannerHeight } = useTopChromeOffset();
  const lastMainTab = useLastMainTabOptional();
  const { count: supportUnreadCount } = useSupportUnreadCount();
  const supportUnread = supportUnreadCount ?? 0;
  const { count: pendingOrdersCount, inboxBadge } = usePendingOrdersCount();
  const pendingOrders = pendingOrdersCount ?? 0;
  const ordersInboxBadge = inboxBadge ?? pendingOrders;
  const { openCount: kitchenOpenCount } = useKitchenTicketCount();
  const kitchenBadge = kitchenOpenCount ?? 0;
  const headerTopInset =
    Platform.OS === 'web' || globalBannerHeight > 0 ? 0 : insets.top;
  // Title vive en el PaperSheet del módulo (mobile A / desktop B).
  // Desktop sidebar: sin barra. Mobile / web angosto: slim sucursal + sync.
  const showChromeBar = !hideTabBarForSidebar;
  const headerBlockHeight = showChromeBar
    ? headerTopInset + getOperationalHeaderBarHeight(false)
    : 0;

  // Evita que el sidebar marque POS (u otro default viejo) antes del evento focus de RN.
  useEffect(() => {
    setActiveTabName(initialMainTabRoute);
  }, [initialMainTabRoute, setActiveTabName]);

  return (
    <View style={styles.tabsShell}>
      {showChromeBar ? (
        <View
          style={[
            styles.headerRegion,
            {
              height: headerBlockHeight,
              paddingTop: headerTopInset,
            },
          ]}
        >
          <AppTabHeader branchName={currentBranchName} />
        </View>
      ) : null}
      <View style={styles.tabsNavigator}>
      <Tab.Navigator
        initialRouteName={initialMainTabRoute}
        safeAreaInsets={{
          top: 0,
          right: insets.right,
          bottom: 0,
          left: insets.left,
        }}
        screenOptions={() => ({
          headerShown: false,
          // Web: desmontar tabs inactivas — con false, POS u otras pantallas quedaban en el DOM
          // encima de Inicio y bloqueaban clicks (tilda al volver o incluso al arrancar).
          ...(Platform.OS === 'web' ? { detachInactiveScreens: true, freezeOnBlur: true } : {}),
          sceneStyle: { backgroundColor: ticketColors.bg },
          sceneContainerStyle: { backgroundColor: ticketColors.bg, flex: 1 },
          tabBarStyle: hideTabBarForSidebar
            ? { display: 'none' }
            : {
                backgroundColor: ticketColors.counter,
                borderTopColor: ticketColors.ruleSoft,
                borderTopWidth: 1,
                height: tabBarHeight,
                paddingTop: tabBarPaddingTop,
                paddingBottom: tabBarBottomInset,
              },
          tabBarActiveTintColor: ticketColors.gold,
          tabBarInactiveTintColor: ticketColors.ink2,
          tabBarLabelStyle: {
            fontSize: 11,
            fontWeight: '600',
            fontFamily: ticketFonts.sans,
          },
        })}
    >
      {tabScreens.map(({ item, hidden }) => {
        const component = TAB_COMPONENTS[item.route];
        if (!component) return null;
        const isMoreTab = item.route === MOBILE_MORE_TAB_ROUTE;
        const isOrdersTab = item.route === 'OrdersInbox';
        const isKitchenTab = item.route === 'KitchenQueue';
        return (
          <Tab.Screen
            key={item.route}
            name={item.route}
            component={component}
            listeners={{
              focus: () => {
                setActiveTabName(item.route);
                if (!hidden) {
                  lastMainTab?.rememberTab(item.route, item.label);
                }
              },
            }}
            options={{
              title: item.label,
              ...(hidden
                ? {
                    tabBarButton: () => null,
                    tabBarItemStyle: {
                      display: 'none',
                      width: 0,
                      maxWidth: 0,
                      flex: 0,
                      overflow: 'hidden',
                    },
                  }
                : {
                    tabBarIcon: ({ color, size }) =>
                      isMoreTab ? (
                        <SupportUnreadIconAnchor count={supportUnread} badgeStyle={moreTabBadgeOffset}>
                          <Ionicons name={item.icon} color={color} size={size} />
                        </SupportUnreadIconAnchor>
                      ) : isOrdersTab ? (
                        <SupportUnreadIconAnchor count={ordersInboxBadge} badgeStyle={moreTabBadgeOffset}>
                          <Ionicons name={item.icon} color={color} size={size} />
                        </SupportUnreadIconAnchor>
                      ) : (
                        <Ionicons name={item.icon} color={color} size={size} />
                      ),
                    tabBarBadge: isKitchenTab ? (kitchenBadge > 0 ? String(kitchenBadge) : undefined) : undefined,
                    ...(isMoreTab ? { tabBarButton: (props) => <MoreTabBarButton {...props} /> } : {}),
                  }),
            }}
          />
        );
      })}
      </Tab.Navigator>
      </View>
    </View>
  );
}

export const MainTabs = (props: MainTabsProps) => <MainTabsInner {...props} />;

const styles = StyleSheet.create({
  tabsShell: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  headerRegion: {
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  tabsNavigator: {
    flex: 1,
    minHeight: 0,
  },
});

const moreTabBadgeOffset = { right: -8 };
